import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format as formatDate } from 'date-fns';
import { API_TAGS } from '@/shared/api';
import { downloadFileFromBlob } from '@/shared/lib/utils/file';
import { searchKeys } from '../keys';
import { deleteConversations, downloadConversationsSearch, runConversationsPipeline, searchConversations } from './api';
import type { SearchConversationRequest } from './types';

export const useSearchConversationsQuery = (request: SearchConversationRequest) => {
  return useQuery({
    queryKey: searchKeys.query(request),
    queryFn: () => searchConversations(request),
    retry: false,
    placeholderData: keepPreviousData,
  });
};

type InfiniteParams = Omit<SearchConversationRequest, 'limit' | 'offset'> & { pageSize?: number };

export const useSearchConversationsInfiniteQuery = (params: InfiniteParams) => {
  const pageSize = params.pageSize ?? 50;

  return useInfiniteQuery({
    queryKey: searchKeys.infiniteQuery({ ...params, pageSize }),
    queryFn: ({ pageParam }) =>
      searchConversations({
        ...params,
        limit: pageSize,
        offset: (pageParam as number) * pageSize,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = ((lastPageParam as number) + 1) * pageSize;
      return lastPage.total > nextOffset ? lastPageParam + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    retry: false,
  });
};

/**
 * Выгрузка найденных диалогов файлом: сохраняет пришедший файл на диск.
 * `isPending` держится всё время формирования файла — им блокируется повторный запуск.
 */
export const useDownloadConversationsSearch = () => {
  return useMutation({
    mutationFn: downloadConversationsSearch,
    onSuccess: (blob) => {
      // Имя повторяет то, которое бэкенд ставит в Content-Disposition: кросс-доменный ответ
      // этот заголовок браузеру не открывает, а из самого файла имя не восстановить.
      downloadFileFromBlob(blob, `dialogs_${formatDate(new Date(), 'yyyy-MM-dd')}.xlsx`);
    },
  });
};

/**
 * Массовый запуск обработки диалогов текущей выборки.
 * `isPending` держится всё время запроса — им блокируется повторный запуск.
 */
export const useRunConversationsPipeline = () => {
  return useMutation({
    mutationFn: runConversationsPipeline,
  });
};

/**
 * Удаление выбранных диалогов. `isPending` держится всё время запроса — им блокируется повторное
 * удаление; об успехе сообщает вызывающий.
 *
 * Удалённый диалог уходит отовсюду, где был виден: из выдачи поиска, списков и карточек диалогов
 * (всё это — под тегом диалогов) и из состава выборок. Кеши диалогов только обесцениваются, без
 * перезапроса открытых: карточка удалённого диалога в этот момент ещё на экране, и её перезапрос
 * ответил бы ошибкой. Показанный список перезапрашивает вызывающий.
 */
export const useDeleteConversations = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteConversations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_TAGS.DIALOGS], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: [API_TAGS.SAMPLES] });
    },
  });
};
